import { makeAxiosClient } from 'lib/api/ssapi/secondStepClient'
import { domains, isCurriculumDraftMode } from 'config/env'
import { formatError } from 'utils/formatError'
import { actions } from './reducer'
import { createCurriculumCourseStateKey } from './selectors'

const getClient = () => makeAxiosClient(domains.CURRICULUM_SERVICE)

const CANONICAL_COURSE_CODE_BY_PROGRAM = {
  k5SelDigital: {
    K: 'k_core_tier-1_siteEducator',
    '1': '1_core_tier-1_siteEducator',
    '2': '2_core_tier-1_siteEducator',
    '3': '3_core_tier-1_siteEducator',
    '4': '4_core_tier-1_siteEducator',
    '5': '5_core_tier-1_siteEducator',
    'k-5_training_tier1': 'k-5_core-training_tier-1_educator',
    'k-2_curriculum_tier2': 'k-2_core_tier-2_siteEducator',
    '3-5_curriculum_tier2': '3-5_core_tier-2_siteEducator',
  },
  mspDigital: {
    '6': '6_core_tier-1_siteEducator',
    '7': '7_core_tier-1_siteEducator',
    '8': '8_core_tier-1_siteEducator',
    '6-8_training_tier1': '6-8_core-training_tier-1_educator',
    '6-8_curriculum_tier2': '6-8_core_tier-2_siteEducator',
  },
  highSchool: {
    highschool: '9-12_core_tier-1_siteEducator',
  },
}

function resolveCanonicalCourseCode(contentGroupCode, courseCode) {
  if (!contentGroupCode || !courseCode) return courseCode
  return (
    CANONICAL_COURSE_CODE_BY_PROGRAM?.[contentGroupCode]?.[courseCode] ||
    courseCode
  )
}

/**
 * Fetch a single course tree from the curriculum service
 * GET /v1/audienced-course-versions endpoint (Mode B — fresh resolution).
 *
 * Replaces the legacy `@cfc/course-manager-sdk` getCourseTree call.
 * Audience inputs come from SSAPI via the bearer JWT (attached by
 * `makeAxiosClient`); the service expects query string params, not a body.
 *
 * Optional `includePlaceholders` is normalized to the string form ("true"/
 * "false") that curriculum-service's lenient boolean query parser expects.
 */
const fetchCurriculumCourseTree = ({
  contentGroupCode,
  courseCode,
  siteId,
  resolutionMode = 'singleSite',
  includePlaceholders,
  audienceMapVersion,
}) => {
  return async dispatch => {
    const courseStateKey = createCurriculumCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    dispatch(actions.fetchingCourseTree({ courseStateKey }))

    try {
      const client = getClient()
      const canonicalCourseCode = resolveCanonicalCourseCode(
        contentGroupCode,
        courseCode,
      )
      const params = {
        resolutionMode,
        contentGroupCode,
        courseCode: canonicalCourseCode,
        ...(siteId != null ? { siteId } : {}),
        ...(includePlaceholders != null
          ? { includePlaceholders: String(includePlaceholders) }
          : {}),
        ...(audienceMapVersion ? { audienceMapVersion } : {}),
        // In non-prod/non-staging envs the curriculum-service seeded content
        // groups are in DRAFT status; ask the service to resolve from
        // draft-or-published so local/dev/test work end-to-end without
        // requiring publishing pipelines to run first.
        ...(isCurriculumDraftMode() ? { draftMode: 'true' } : {}),
      }

      const response = await client.get('/v1/audienced-course-versions', {
        params,
      })
      dispatch(
        actions.fetchCourseTreeSuccess({
          courseStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchCourseTreeError({
          courseStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

/**
 * Fetches a course tree using params from a coursePointer returned by
 * GET /v1/content. The coursePointer.query may use either camelCase keys
 * (per the curriculum-service schema) or snake_case keys (legacy wire format);
 * both are handled here.
 *
 * @param {Object} options
 * @param {Object} options.coursePointer    - coursePointer from /v1/content response
 * @param {string} options.contentGroupCode - used as the state-key prefix (e.g. 'dwb')
 * @param {number|null} options.siteId      - active site id
 */
const fetchCourseTreeFromPointer = ({
  coursePointer,
  contentGroupCode,
  siteId,
}) => {
  return async dispatch => {
    const query = coursePointer?.query || {}

    // Normalise coursePointer query — handle camelCase (schema) and snake_case (wire)
    const courseCode = query.courseCode || query.course_code
    const contentGroupVersionId =
      query.contentGroupVersionId ?? query.content_group_version_id ?? null
    const programId = query.programId ?? query.program_id ?? null
    const pointerDraftMode = query.draftMode || query.draft_mode || null

    if (!courseCode) {
      console.error(
        '[fetchCourseTreeFromPointer] courseCode missing from coursePointer.query',
      )
      return
    }

    const courseStateKey = createCurriculumCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    dispatch(actions.fetchingCourseTree({ courseStateKey }))

    try {
      const client = getClient()
      const params = {
        resolutionMode: 'singleSite',
        courseCode,
        ...(contentGroupVersionId != null
          ? { contentGroupVersionId }
          : { contentGroupCode }),
        ...(siteId != null ? { siteId } : {}),
        ...(programId != null ? { programId } : {}),
        ...(pointerDraftMode ? { draftMode: pointerDraftMode } : {}),
        ...(isCurriculumDraftMode() ? { draftMode: 'true' } : {}),
      }

      const response = await client.get('/v1/audienced-course-versions', {
        params,
      })
      dispatch(
        actions.fetchCourseTreeSuccess({
          courseStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchCourseTreeError({
          courseStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

const clearErrors = () => {
  return async dispatch => {
    dispatch(actions.clearErrors())
  }
}

export default {
  fetchCurriculumCourseTree,
  fetchCourseTreeFromPointer,
  clearErrors,
}
